import {createStore, filterNil} from '@ngneat/elf';
import {selectEntity, updateEntities, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {FeilhåndteringArgumenter, Feilhåndteringsstrategi, FeilhåndteringsstrategiForDataOgMeta, hentFraLokallagerEllerNull, MedId, registrerEntitet, serverFraLagerOgRequestPakketIMeta, serverFraRequest, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {firstValueFrom, Observable} from "rxjs";
import {ModelloppdateringType, MøteId, MøtetranskripsjonRessursClient, OppsummertTranskribertMøtereferatModell, ProtokollDetaljerModell, ProtokollRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {DataOgMeta, kunRequestRespons, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {HttpResponse} from "@angular/common/http";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {map, tap} from "rxjs/operators";
import {tomProtokollTranskripsjonInformasjonModell} from "felles-ng2/modell/tommeModeller";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {GenerertMøtereferat, somGenerertMøtereferat} from "felles-ng2/modell/transkripsjonModellHjelpere";

const debug = initDebugger('ProtokollDatatjeneste');

/** Datalager for {@link ProtokollDetaljerModell}. Bruker {@link MøteId} som id ( id = møteId ). */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class ProtokollDatatjeneste {

    /** Lager for {@link ProtokollDetaljerDLM}.
     * Uten forelder, da vi ikke gjør oppslag mot noen logisk forelder */
      //public for testbarhet
    readonly protokollLager = createStore({name: 'protokolldetaljer'}, withEntities<ProtokollDetaljerDLM>());

    /** Holder rede på åpne subscriptions basert på møteId. Benyttes ifbm modelloppdateringer. */
      //public for testbarhet
    readonly møteAbonnenter = new DatalagerAbonnenter<MøteId>('protokolldetaljer.id');

    constructor(private protokollRessurs: ProtokollRessursClient,
                private møtetranskripsjonRessurs: MøtetranskripsjonRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdateringer: Modelloppdateringer) {
        this.lyttTilModelloppdateringer();
    }

    /** Returnerer protokolldetaljer for angitt møte fra lokallager, eller null dersom ikke funnet */
    hentFraLokallager(møteId: MøteId): ProtokollDetaljerDLM | null {
        return hentFraLokallagerEllerNull(this.protokollLager, møteId);
    }

    /** Laster protokolldetaljer for angitt møte, levert fra lokallager og fra server, pakket i {@link DataOgMeta} */
    hentMedMeta(møteId: MøteId, feilhåndteringsstrategi: FeilhåndteringsstrategiForDataOgMeta = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ProtokollDetaljerDLM>> {
        return this.møteAbonnenter.observer(møteId,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.protokollRessurs.hent(møteId),
              registrerILager: (respons: HttpResponse<ProtokollDetaljerModell>) => this.erstattILokallager(respons.body!, møteId),
              hentFraLager: () => this.protokollLager.pipe(
                selectEntity(møteId),
                filterNil(),
                distinctUntilChangedDeep() //kun lever verdier fra lageret ved reelle endringer
              ),
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke hente protokollinformasjon')), 'hentMedMeta')
          }));
    }

    /** Laster protokolldetaljer for angitt møte, levert fra lokallager og fra server */
    hent(møteId: MøteId, feilhåndteringsstrategi: Feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<ProtokollDetaljerDLM> {
        return this.hentMedMeta(møteId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    oppsummerMøtebehandlingsopptak(møteId: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<OppsummertTranskribertMøtereferatModell> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.møtetranskripsjonRessurs.oppsummerOpptak(møteId).råRespons()
              .pipe(tap(respons => this.protokollLager.update(updateEntities(møteId, protokolldetaljer => ({
                  ...protokolldetaljer,
                  transkripsjonInformasjon: {
                      ...protokolldetaljer.transkripsjonInformasjon,
                      oppsummertMoetereferatTilgjengelig: true // <-- muterer dette feltet for å indikere at generert protokoll nå er tilgjengelig
                  }
              }))))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke generere forslag til protokoll fra lydopptakene av møtet')), 'oppsummerMøtebehandlingsopptak')
        }));
    }

    /**
     * Henter oppsummert transkripsjon av møtebehandlingen for angitt møte som ett {@link GenerertSaksreferat} per sak.
     *
     * Saksreferatene parres med `saker`, som er sakene på møtet. Innhold som ikke lot seg knytte til
     * noen av dem ligger under {@link GenerertMøtereferat#utenSak}.
     */
    async genererSaksreferaterFraOpptak(møteId: MøteId, saker: AgendaelementModellDLM[]): Promise<GenerertMøtereferat> {
        const oppsummering = await this.oppsummerMøtebehandlingsopptak(møteId);
        return somGenerertMøtereferat(oppsummering, saker);
    }

    /** Laster protokolldetaljer for angitt møte fra server og oppdaterer lokalt datalager med disse. */
    async relast(møteId: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<void> {
        await firstValueFrom(this.hentMedMeta(møteId, feilhåndteringsstrategi).pipe(kunRequestRespons()));
    }

    /** Erstatter protokolldetaljer knyttet til angitt møte i lokallager */
    erstattILokallager(protokoll: ProtokollDetaljerModell, møteId: MøteId): ProtokollDetaljerDLM {
        debug('erstattILokallager', møteId);
        return registrerEntitet(this.protokollLager, this.somProtokollDetaljerDLM(protokoll, møteId));
    }

    /**
     * Sletter lydopptakene av møtebehandlingen og det oppsummerte møtereferatet på server.
     *
     * Oppdaterer {@link ProtokollDetaljerModell#transkripsjonInformasjon}
     */
    async slettOpptak(møteId: MøteId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.møtetranskripsjonRessurs.slettOpptak(møteId).råRespons()
              .pipe(tap(respons => this.protokollLager.update(updateEntities(møteId, {transkripsjonInformasjon: tomProtokollTranskripsjonInformasjonModell()})))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette opptak')), 'slettOpptak')
        }));
    }

    //hjelpemetoder

    private somProtokollDetaljerDLM(protokoll: ProtokollDetaljerModell, møteId: MøteId): ProtokollDetaljerDLM {
        return {...protokoll, id: møteId};
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        this.lyttTilOpptakTranskribert();
        this.lyttTilTranskripsjonOppsummert();
        this.lyttTilOpptakSlettet();
    }

    /**
     * Når et lydopptak av møtebehandlingen er transkribert har protokollens
     * transkripsjonsinformasjon endret seg, og protokolldetaljene relastes.
     */
    private lyttTilOpptakTranskribert() {
        this.opprettetEllerOppdatertProtokollHendelseMedMøteId(ModelloppdateringType.MØTEPROTOKOLL_OPPTAK_TRANSKRIBERT)
          .oppdaterLagerVedHendelse();
    }

    /**
     * Når transkripsjonene er oppsummert til et møtereferat har protokollens
     * transkripsjonsinformasjon endret seg, og protokolldetaljene relastes.
     */
    private lyttTilTranskripsjonOppsummert() {
        this.opprettetEllerOppdatertProtokollHendelseMedMøteId(ModelloppdateringType.MØTEPROTOKOLL_TRANSKRIPSJON_OPPSUMMERT)
          .oppdaterLagerVedHendelse();
    }

    /**
     * Når lydopptakene av møtebehandlingen og det oppsummerte møtereferatet er slettet har
     * protokollens transkripsjonsinformasjon endret seg, og protokolldetaljene relastes.
     */
    private lyttTilOpptakSlettet() {
        this.opprettetEllerOppdatertProtokollHendelseMedMøteId(ModelloppdateringType.MØTEPROTOKOLL_OPPTAK_SLETTET)
          .oppdaterLagerVedHendelse();
    }

    /**
     * Håndtering av hendelser der hendelsemodellen er {@link MøteId} og protokolldetaljene for
     * møtet skal relastes.
     *
     * Merk at relasting kun skjer dersom noen faktisk abonnerer på protokollen for møtet,
     * se {@link møteAbonnenter}.
     */
    private opprettetEllerOppdatertProtokollHendelseMedMøteId(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<MøteId, ProtokollDetaljerDLM>(this.protokollLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(møteId => møteId)
          .medEntitetLaster(møteId => this.hentSomDLMRespons(møteId))
          .medAbonnentoppslag(this.møteAbonnenter, møteId => møteId);
    }

    /** Henter protokolldetaljer for angitt møte som lager-entitet, dvs med 'id' satt til møteId */
    private hentSomDLMRespons(møteId: MøteId): Observable<HttpResponse<ProtokollDetaljerDLM>> {
        return this.protokollRessurs.hent(møteId).råRespons()
          .pipe(map(respons => respons.clone({body: this.somProtokollDetaljerDLM(respons.body!, møteId)})));
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }
}

//hjelpefunksjoner

//eksporterte typer

/** Datalager-spesifikk variant av {@link ProtokollDetaljerModell} som utvider denne med et
 * 'id' felt satt til {@link MøteId} for møtet protokollen tilhører.
 * Nødvendig fordi modellen selv ikke har noe id-felt, og det finnes kun én protokoll per møte. */
export type ProtokollDetaljerDLM = ProtokollDetaljerModell & MedId<MøteId>;

